import { useCo2MaterialTypeByCategory } from "@api/co2MaterialTypes";
import DataGrid from "@components/DataGrid/DataGrid";
import i18n from "@i18n/i18n";
import { alpha, Box, darken, lighten, Theme, useTheme } from "@mui/material";
import { GridColDef, GridToolbarContainer, GridToolbarExport } from "@mui/x-data-grid-premium";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import useResizeObserver from "use-resize-observer";

const Co2MaterialTypeByCategoryGrid = observer(() => {
  const theme = useTheme();
  const [rows, setRows] = useState<any[]>([]);
  const containerRef = useRef(null);
  const { height } = useResizeObserver({ ref: containerRef });
  const co2MaterialTypeByCategory = useCo2MaterialTypeByCategory();

  useEffect(() => {
    const newRows = co2MaterialTypeByCategory.data?.map((material, index) => ({ ...material, id: index })) || [];
    setRows(newRows);
  }, [co2MaterialTypeByCategory.data]);

  const columns: GridColDef[] = [
    {
      field: "cadModelCategoryName",

      headerName: i18n.t("aggregated-data-tab.grid.columns.cad-model-category"),
      editable: false,
      flex: 1,
    },
    {
      field: "co2MaterialTypeName",
      headerName: i18n.t("aggregated-data-tab.grid.columns.ec-material"),
      editable: false,
      flex: 1,
    },
    {
      field: "count",
      headerName: i18n.t("aggregated-data-tab.grid.columns.no-of-elements"),
      description: i18n.t("aggregated-data-tab.grid.columns-tooltip.no-of-elements"),
      editable: false,
      flex: 1,
    },
  ];

  const ExcelExportToolbar = () => {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: 1,
          borderBottom: 1,
          backgroundColor: getBackgroundColor(theme),
          borderColor: getBorderColor(theme),
          fontWeight: "bold",
        }}
      >
        <GridToolbarExport csvOptions={{ disableToolbarButton: true }} printOptions={{ disableToolbarButton: true }} />
      </GridToolbarContainer>
    );
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%", height: "100%" }}>
      <DataGrid
        loading={co2MaterialTypeByCategory.isFetching || co2MaterialTypeByCategory.isPending}
        columns={columns}
        rows={rows}
        disableColumnMenu
        density="compact"
        hideFooter={false}
        initialState={{
          sorting: {
            sortModel: [{ field: "value", sort: "asc" }],
          },
        }}
        slots={{
          toolbar: ExcelExportToolbar,
        }}
        sx={{ height: `${(height || 400) - 16}px` }}
      />
    </Box>
  );
});

const getBorderColor = (theme: any) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.background.default, 1), 0.88);
  }
  return darken(alpha(theme.palette.background.default, 1), 0.68);
};

const getBackgroundColor = (theme: Theme) => {
  if (theme.palette.mode === "light") {
    return lighten(alpha(theme.palette.divider, 1), 0.92);
  }
  return darken(alpha(theme.palette.divider, 1), 0.82);
};

export default Co2MaterialTypeByCategoryGrid;
